import { Box, Popover, Typography } from "@mui/material";

import { curingColour, curingLabel } from "../../api/curing";

export interface CuringPointReading {
  available: boolean;
  curing: number | null;
  confidence: number | null;
  observed_on: string | null;
  source?: "satellite" | "weather_model" | null;
  calibrated_by?: number | null;
  reason?: string | null;
}

interface CuringPointPopoverProps {
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  reading: CuringPointReading | null;
}

/**
 * Curing at the spot the user clicked.
 *
 * Doubles as the legend: the swatch and wording say what the colour under the
 * cursor means, which is more useful than a static key in the corner because
 * it answers the question about the place actually being looked at.
 */
const CuringPointPopover: React.FC<CuringPointPopoverProps> = ({
  anchorPosition,
  onClose,
  reading,
}) => {
  if (!anchorPosition) return null;

  const pct = reading?.curing ?? null;
  const confidence = reading?.confidence ?? null;
  const modelled = reading?.source === "weather_model";
  const lowConfidence = !modelled && confidence !== null && confidence < 60;
  const calibratedBy = reading?.calibrated_by ?? 0;

  return (
    <Popover
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { bgcolor: "#1a1d2e", color: "#fff", p: 2, minWidth: 210 } }}
    >
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
        {modelled ? "Pine understorey curing here" : "Grass curing here"}
      </Typography>

      {pct !== null && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Box
              sx={{
                width: 18, height: 18, borderRadius: "3px",
                bgcolor: curingColour(pct),
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {pct}%
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              {curingLabel(pct)}
            </Typography>
          </Box>

          {modelled ? (
            <>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 1 }}
              >
                Modelled from rain and evaporation to {reading?.observed_on}.
                The satellite can&apos;t see the grass under the canopy.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: calibratedBy > 0 ? "rgba(255,255,255,0.5)" : "#ffb74d",
                  display: "block", mt: 0.5,
                }}
              >
                {calibratedBy > 0
                  ? `Level set by ${calibratedBy} observation${calibratedBy === 1 ? "" : "s"} in the pine.`
                  : "Not yet checked on the ground. Record an observation in the pine to set the level."}
              </Typography>
            </>
          ) : (
            reading?.observed_on && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 1 }}
              >
                Satellite view from {reading.observed_on}
              </Typography>
            )
          )}

          {lowConfidence && (
            <Typography
              variant="caption"
              sx={{ color: "#ffb74d", display: "block", mt: 1 }}
            >
              Tree cover here, so the satellite is partly seeing the canopy
              rather than the grass beneath it. Treat this as indicative.
            </Typography>
          )}
        </>
      )}
    </Popover>
  );
};

export default CuringPointPopover;
